import { useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { resolveImg } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ settings }) => {
  const { lang, t, pick } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const headline = pick(settings, "hero_headline");
  const words = headline.split(" ");
  const imgs = (settings?.hero_images || []).map(resolveImg);

  return (
    <section id="top" ref={ref} className="relative min-h-screen pt-32 md:pt-40 pb-16 overflow-hidden">
      {/* ambient shapes */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-[38rem] h-[38rem] rounded-full bg-maize/20 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-32 w-[34rem] h-[34rem] rounded-full bg-terracotta/10 blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
        {/* left: kinetic type */}
        <motion.div style={{ opacity }}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="overline text-terracotta mb-6 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-terracotta inline-block" />
            {pick(settings, "tagline")}
          </motion.p>

          <AnimatePresence mode="wait">
            <motion.h1
              key={lang}
              className="font-serif font-black tracking-tight text-ink text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95]"
            >
              {words.map((w, i) => {
                const accent = i >= words.length - 2 && words.length > 2;
                return (
                  <MaskLine key={`${lang}-${i}`} delay={0.15 + i * 0.08}>
                    {accent ? (
                      <motion.span
                        className="hero-accent"
                        whileHover={{ skewX: -6, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300, damping: 15 }}
                      >
                        {w}
                      </motion.span>
                    ) : (
                      w
                    )}{" "}
                  </MaskLine>
                );
              })}
            </motion.h1>
          </AnimatePresence>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.9 }}
            className="font-body text-ink/70 text-base md:text-lg max-w-xl mt-8 leading-relaxed"
          >
            {pick(settings, "hero_sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <button
              onClick={() => scrollToId("menu")}
              data-testid="hero-menu-btn"
              className="font-label font-bold tracking-wide bg-ink text-dough rounded-full px-7 py-4 hover:bg-terracotta transition-colors duration-300"
            >
              {t("view_menu")}
            </button>
            <button
              onClick={() => scrollToId("visit")}
              data-testid="hero-order-btn"
              className="font-label font-bold tracking-wide border border-ink/30 text-ink rounded-full px-7 py-4 hover:bg-ink hover:text-dough transition-colors duration-300"
            >
              {t("order")}
            </button>
            <a href={`tel:${(settings?.phone_1 || "").replace(/[^0-9]/g, "")}`} className="font-label font-medium text-ink/70 link-underline ml-1">
              {settings?.phone_1}
            </a>
          </motion.div>
        </motion.div>

        {/* right: floating arch collage */}
        <div className="relative h-[460px] sm:h-[560px] lg:h-[640px]">
          {imgs[0] && (
            <motion.div style={{ y: y1 }} className="absolute right-0 top-0 w-[62%] h-[70%] arch shadow-2xl shadow-ink/20">
              <motion.img
                src={imgs[0]} alt="dish"
                initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.4 }}
                className="w-full h-full object-cover"
              />
            </motion.div>
          )}
          {imgs[1] && (
            <motion.div style={{ y: y2 }} className="absolute left-0 top-[26%] w-[48%] h-[46%] rounded-[14px] overflow-hidden shadow-2xl shadow-ink/20 ring-4 ring-dough">
              <motion.img
                src={imgs[1]} alt="dish"
                initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.6 }}
                className="w-full h-full object-cover"
              />
            </motion.div>
          )}
          {imgs[2] && (
            <motion.div style={{ y: y3 }} className="absolute right-[8%] bottom-0 w-[42%] h-[38%] rounded-full overflow-hidden shadow-2xl shadow-ink/20 ring-4 ring-dough">
              <motion.img
                src={imgs[2]} alt="dish"
                initial={{ scale: 1.2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.8 }}
                className="w-full h-full object-cover"
              />
            </motion.div>
          )}
          <motion.img
            src={resolveImg(settings?.logo_url)}
            alt="logo"
            initial={{ scale: 0, rotate: -30, opacity: 0 }}
            animate={{
              scale: 1,
              rotate: 0,
              opacity: 1,
              y: [0, -14, 0],
            }}
            transition={{
              scale: { duration: 1, delay: 1, ease: [0.22, 1, 0.36, 1] },
              rotate: { duration: 1, delay: 1, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.8, delay: 1 },
              y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.6 },
            }}
            whileHover={{ scale: 1.06, rotate: 4 }}
            data-testid="hero-logo-badge"
            className="absolute left-[0%] bottom-[6%] w-60 h-60 md:w-72 md:h-72 rounded-full object-cover ring-4 ring-dough shadow-2xl shadow-ink/30 z-20"
          />
        </div>
      </div>
    </section>
  );
};
